import { useEffect, useMemo, useRef, useState } from 'react';
import { fromJS, List, Map } from 'immutable';

import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import useMeetingMembers from './MeetingParticipantsList/useMeetingMembers';

import BaseModal, { modalSizes } from 'flow/components/ui-components/BaseModal';
import InputField from 'flow/components/ui-components/InputField';
import Loading from 'flow/components/ui-components/Loading';
import ParticipantPicker from 'flow/components/modals/VideoMeetingRoom/ParticipantPicker';

import { setSnackbar } from 'flow/actions/app-actions';
import {
    createRoomSession,
    fetchChatSession,
    inviteChatMembers,
    updateChatSession,
} from 'flow/actions/chat-actions';
import {
    linkChatSessionToMeeting,
    setActiveMeetingChatHistoryActivatedAction,
} from 'flow/actions/meeting-actions';
import {
    selectActiveMeetingChatSessionId,
    selectActiveMeetingCode,
} from 'flow/selectors/meeting-selectors';
import { roomTypes } from 'flow/utils/message-utils-TW';
import { getExtensions } from 'flow/selectors/ExtensionSelectors';

import { ChatSessionKey, ExtensionKey } from 'flow/types/EntityKeys';

type Props = {
    meetingName: string;
    onClose: () => void;
};

const ActivateChatHistoryModal = ({ meetingName, onClose }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { ownExtension } = useExtension();
    const ownKey = ownExtension?.get('key');

    const { members } = useMeetingMembers();
    const extensions = useAppSelector(getExtensions);
    const meetingCode = useAppSelector(selectActiveMeetingCode);

    const chatSessionId = useAppSelector(selectActiveMeetingChatSessionId);
    const existingSessionKey = chatSessionId
        ? (`chatsession-${chatSessionId}` as ChatSessionKey)
        : null;
    const existingSession = useAppSelector((state) =>
        existingSessionKey
            ? state.chats.getIn(['sessions', existingSessionKey])
            : null,
    );
    const existingSessionLoading = !!existingSessionKey && !existingSession;

    useEffect(() => {
        if (existingSessionKey && !existingSession) {
            dispatch(fetchChatSession(existingSessionKey, 'contact'));
        }
    }, [existingSessionKey, existingSession, dispatch]);

    const liveInternalKeys = useMemo((): ExtensionKey[] => {
        if (!extensions) return [];

        const extKeyByChatUserKey = extensions.reduce((acc, ext) => {
            const chatUserKey = ext.get('chatUserKey');
            return chatUserKey ? acc.set(chatUserKey, ext.get('key')) : acc;
        }, Map<string, ExtensionKey>());

        return members
            .filter((m) => !m.memberIsSelf && m.isInternal)
            .flatMap((m) => {
                const extKey = extKeyByChatUserKey.get(m.chatUserKey);
                return extKey ? [extKey] : [];
            });
    }, [members, extensions]);

    const [name, setName] = useState(meetingName);
    const [selectedKeys, setSelectedKeys] =
        useState<ExtensionKey[]>(liveInternalKeys);
    const [submitting, setSubmitting] = useState(false);
    const [pendingSessionKey, setPendingSessionKey] = useState<string | null>(
        null,
    );

    const seeded = useRef(selectedKeys.length > 0);
    useEffect(() => {
        if (!seeded.current && liveInternalKeys.length > 0) {
            setSelectedKeys(liveInternalKeys);
            seeded.current = true;
        }
    }, [liveInternalKeys]);

    const handleSelectedKeysChange = (keys: ExtensionKey[]) => {
        seeded.current = true;
        setSelectedKeys(keys);
    };

    const handleCreateError = (errorMessage: string) => {
        dispatch(
            setSnackbar({ i18nextMessage: errorMessage || 'snackbar.error' }),
        );
        setSubmitting(false);
    };

    const linkNewSession = (newSessionKey: string) => {
        setPendingSessionKey(newSessionKey);
        if (!meetingCode) {
            onClose();
            return;
        }
        dispatch(
            linkChatSessionToMeeting(meetingCode, newSessionKey, {
                onSuccess: onClose,
                onError: () => setSubmitting(false),
            }),
        );
    };

    const handleSubmit = () => {
        if (!ownExtension || submitting || existingSessionLoading) return;
        setSubmitting(true);

        if (pendingSessionKey) {
            linkNewSession(pendingSessionKey);
            return;
        }

        const trimmedName = name.trim();

        const memberMaps = selectedKeys.flatMap((key) => {
            const chatUserKey = extensions.get(key)?.get('chatUserKey');
            return chatUserKey ? [Map({ key: chatUserKey })] : [];
        });

        const chatUserKeys = List([
            ...memberMaps,
            Map({ key: ownExtension.get('chatUserKey') }),
        ]);

        if (existingSessionKey && existingSession) {
            const existingInternalMembers = existingSession
                .get('members')
                .filter((member) => !!member.get('extensionKey'));
            const existingMemberKeys = new Set(
                existingInternalMembers
                    .map((member) => member.get('key'))
                    .toArray(),
            );
            const newMemberDTOs = chatUserKeys
                .filter((member) => !existingMemberKeys.has(member.get('key')))
                .toJS();

            dispatch(
                updateChatSession(
                    existingSessionKey,
                    {
                        key: existingSessionKey,
                        roomType: roomTypes.room,
                        name: trimmedName,
                        members: existingInternalMembers,
                    },
                    () => {
                        if (newMemberDTOs.length > 0) {
                            dispatch(
                                inviteChatMembers(
                                    existingSessionKey,
                                    newMemberDTOs,
                                ),
                            );
                        }
                        dispatch(
                            setActiveMeetingChatHistoryActivatedAction(true),
                        );
                        onClose();
                    },
                ),
            );
            return;
        }

        const sessionDTO = fromJS({
            name: trimmedName,
            roomType: roomTypes.room,
            members: chatUserKeys,
        });

        dispatch(
            createRoomSession(sessionDTO, null, linkNewSession, () =>
                handleCreateError('snackbar.error'),
            ),
        );
    };

    const renderSubmitButtonText = () =>
        submitting ? (
            <Loading size={16} disableTimeout={true} />
        ) : (
            i18next.t('meetings.create_room.submit')
        );

    return (
        <BaseModal
            open={true}
            header={i18next.t('meetings.activate_chat_history.title')}
            modalSize={modalSizes.SMALL}
            confirmButtonText={renderSubmitButtonText()}
            disableSubmit={
                !name.trim() ||
                !ownExtension ||
                submitting ||
                existingSessionLoading
            }
            preventCloseOnSubmit={true}
            onSubmit={handleSubmit}
            onClose={onClose}
        >
            <div className="flex flex-col gap-y-4 pb-2">
                <p className="text-sm text-gray-600">
                    {i18next.t('meetings.create_room.activate_description')}
                </p>

                <InputField
                    label={i18next.t('meetings.create_room.name_label')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoFocus={true}
                    maxLength={32}
                />

                <div className="flex flex-col gap-y-1">
                    <p className="text-xs font-bold text-gray-800">
                        {i18next.t('meetings.create_room.participants_label')}
                    </p>

                    <ParticipantPicker
                        ariaLabel={i18next.t(
                            'meetings.create_room.participants_label',
                        )}
                        selectedKeys={selectedKeys}
                        onChange={handleSelectedKeysChange}
                        ownKey={ownKey}
                    />
                </div>
            </div>
        </BaseModal>
    );
};

export default ActivateChatHistoryModal;
